import React, { useEffect, useState } from 'react';
import { useOktaAuth } from '@okta/okta-react';
import { toRelativeUrl } from '@okta/okta-auth-js';
import { Navigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import store from './../../redux/store';
import persistDataStore from './../../redux/persistStore';
import { ReduxAction, setUserId, Endpoints } from './../../constants';
import {
  getDbConfigData,
  fetchUserPreferences,
  fetchUserContracts,
  // FEATURE DISABLED (Release july-2026)
  // getHighestPriorityGroup,
  // classifyUserScenario,
} from './../../helpers/component-helper-methods';
import {
  setMaxExportSizeLimit,
  contractDisplayOptionKey,
  setGlobalUserType,
  superUserGroup,
  endUserGroup,
  ccaUserGroup,
  csaUserGroup,
  viewerUserGroup,
  exhibitUserGroup,
  UrlPath,
  // FEATURE DISABLED (Release july-2026)
  // PersistReduxAction,
} from './../../constants';

export const SecureRoute = () => {
  const { oktaAuth, authState } = useOktaAuth();
  const [userInfo, setUserInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const dispatch = useDispatch();
  useEffect(() => {
    if (!authState) {
      return;
    }

    if (!authState?.isAuthenticated) {
      const originalUri = toRelativeUrl(
        window.location.href,
        window.location.origin,
      );
      oktaAuth.setOriginalUri(originalUri);
      oktaAuth.signInWithRedirect();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [oktaAuth, !!authState, authState?.isAuthenticated]);

  useEffect(() => {
    let isMounted = true;
    // Call async function to retrieve user info
    let storeData = store.getState();
    let storeUserInfo = storeData?.userInfo;
    if (storeUserInfo && storeUserInfo.userInfo) {
      setUserInfo(storeUserInfo.userInfo);
      setLoading(false);
    } else {
      oktaAuth.token
        .getUserInfo()
        .then(async (userResp) => {
          if (isMounted) {
            userResp.email = userResp.email?.toLowerCase();
            userResp.UserID = userResp.UserID?.toLowerCase();
            userResp.userPreferences = await fetchUserPreferences(
              userResp.email,
              dispatch,
              oktaAuth.getAccessToken(),
            );

            // FEATURE DISABLED (Release july-2026) - Dual-persona / exhibit user login flow
            // const rawGroups = userResp?.SPGGroups ?? [];
            // const userScenario = classifyUserScenario(rawGroups);
            // if (userScenario === 'DUAL_PERSONA') {
            //   userResp.rawSPGGroups = rawGroups;
            //   const onboardedGroups = rawGroups.filter(
            //     (g) => g === ccaUserGroup || g === endUserGroup,
            //   );
            //   const highestOnboarded = getHighestPriorityGroup(onboardedGroups);
            //   const persistedPersona =
            //     persistDataStore.getState()?.activePersona?.selectedPersona;
            //   if (persistedPersona === 'onboarded') {
            //     userResp.SPGGroups = [highestOnboarded].filter(Boolean);
            //     setGlobalUserType(highestOnboarded === ccaUserGroup);
            //   } else if (persistedPersona === 'non-onboarded') {
            //     userResp.SPGGroups = [exhibitUserGroup];
            //     setGlobalUserType(false);
            //   } else {
            //     userResp.SPGGroups = [
            //       highestOnboarded,
            //       exhibitUserGroup,
            //     ].filter(Boolean);
            //   }
            // } else if (rawGroups.length > 0) {
            //   const highestGroup = getHighestPriorityGroup(rawGroups);
            //   if (highestGroup) {
            //     userResp.SPGGroups = [highestGroup];
            //   }
            // }

            setUserInfo(userResp);
            setGlobalUserType(userResp?.SPGGroups?.includes(ccaUserGroup));
            dispatch({ type: ReduxAction.userInfoSet, payload: userResp });
            // Fetch user contracts for exhibit/end users
            // FEATURE DISABLED (Release july-2026) - removed exhibitUserGroup from contract fetch
            if (
              (userResp?.SPGGroups?.includes(endUserGroup)
              // || userResp?.SPGGroups?.includes(exhibitUserGroup)
              ) &&
              !userResp.SPGGroups?.includes(ccaUserGroup) &&
              !userResp.SPGGroups?.includes(csaUserGroup) &&
              !userResp.SPGGroups?.includes(superUserGroup) &&
              !userResp.SPGGroups?.includes(viewerUserGroup)
            ) {
              await fetchUserContracts(
                userResp.email,
                dispatch,
                oktaAuth.getAccessToken(),
              );
            }
            setUserId(userResp.UserID);
            setLoading(false);
          }
        })
        .catch((error) => {
          if (isMounted) {
            setUserInfo(null);
            setLoading(false);
          }
        });
    }
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const fetchConfig = async () => {
      if (authState && userInfo) {
        try {
          const [dbConfig] = await Promise.all([
            getDbConfigData(dispatch, authState.accessToken.accessToken),
          ]);
          setMaxExportSizeLimit(dbConfig?.exportLimit ?? 0);
          setGlobalUserType(userInfo?.SPGGroups?.includes(ccaUserGroup));
        } catch (error) {}
      }
    };
    fetchConfig();
  }, [authState?.accessToken?.accessToken, userInfo?.email, dispatch]);

  if (loading || !authState || !authState?.isAuthenticated) {
    return <div></div>;
  } else if (userInfo) {
    if (
      !userInfo.SPGGroups?.includes(superUserGroup) &&
      !userInfo.SPGGroups?.includes(ccaUserGroup) &&
      !userInfo.SPGGroups?.includes(csaUserGroup) &&
      !userInfo.SPGGroups?.includes(viewerUserGroup) &&
      !userInfo.SPGGroups?.includes(endUserGroup)
      // FEATURE DISABLED (Release july-2026) - exhibit-only users are now blocked
      // && !userInfo.SPGGroups?.includes(exhibitUserGroup)
    ) {
      localStorage.clear();
      oktaAuth.signOut();
      return <div></div>;
    } else {
      localStorage.setItem('userID', userInfo.UserID);
      localStorage.setItem('token', authState.accessToken.accessToken);
      // get and set time for token renewal/auto-logout
      const loginTime = new Date().getTime();
      localStorage.setItem('lastLoginTime', loginTime);
      localStorage.setItem('initialLoginTime', loginTime);
      localStorage.setItem('refreshToken', authState.refreshToken.refreshToken);
      localStorage.setItem('clientId', authState.idToken.clientId);
      localStorage.setItem('expiresAt', authState.idToken.expiresAt);

      // FEATURE DISABLED (Release july-2026) - Dual-persona selection
      // const persistedPersona =
      //   persistDataStore.getState()?.activePersona?.selectedPersona;
      // const hasBothPersonas =
      //   userInfo.SPGGroups?.includes(exhibitUserGroup) &&
      //   (userInfo.SPGGroups?.includes(ccaUserGroup) ||
      //     userInfo.SPGGroups?.includes(endUserGroup));

      // if the user refreshes and already has a token, redirect them to the last page they were on
      let path = localStorage.getItem('currentRoute');
      if (
        localStorage.getItem('token') &&
        path !== null &&
        path !== '/'
      ) {
        return <Navigate to={path} replace={true} />;
      } else {
        // Clear Default Column State once logged in
        localStorage.setItem('columnState', '{}');

        // default page after being authenticated
        if (
          userInfo.SPGGroups.includes(superUserGroup) ||
          userInfo.SPGGroups.includes(csaUserGroup) ||
          userInfo.SPGGroups.includes(viewerUserGroup)
        ) {
          return <Navigate to={'/admin-dashboard'} replace={true} />;
        // FEATURE DISABLED (Release july-2026)
        // } else if (hasBothPersonas) {
        //   return <Navigate to={UrlPath.accountTypeSelection} replace={true} />;
        // } else if (persistedPersona === 'non-onboarded') {
        //   return <Navigate to={UrlPath.userLocation} replace={true} />;
        // FEATURE DISABLED (Release july-2026) - exhibit users are now blocked (fall through to endUser/home)
        // } else if (userInfo.SPGGroups.includes(exhibitUserGroup)) {
        //   return <Navigate to={'/user-location'} replace={true} />;
        } else {
          // FEATURE DISABLED (Release july-2026) - home page removed, redirect to packages
          return <Navigate to={UrlPath.packages} replace={true} />;
        }
      }
    }
  }
};
